"use client";

import React, { useMemo, useState } from "react";
import { ArrowRight, FlaskConical, Factory, Droplets, ShieldCheck, Layers, CircleCheck, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import InlineCta from "@/components/common/InlineCta";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
import type { Lang } from "@/lib/i18n";
interface ApplicationItem {
  id: string;
  category: "chemical" | "polymer" | "water" | "pharma";
  title: Record<Lang, string>;
  description: Record<Lang, string>;
  micronRating: string;
  recommendedMesh: Record<Lang, string>;
  keyFeature: Record<Lang, string>;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: Record<Lang, string>;
  };
  link: string;
}
const applicationsData: ApplicationItem[] = [{
  id: "polymer-extrusion",
  category: "polymer",
  title: {
    en: "Polymer & Plastic Extrusion",
    zh: "高分子与塑料挤出过滤",
    es: "Extrusión de polímeros y plásticos",
    de: "Polymer- & Kunststoffextrusion",
    fr: "Extrusion de polymères et plastiques",
    pt: "Extrusão de polímeros e plásticos"
  },
  description: {
    en: "Continuous melt filtration for blown film, recycled resin pelletizing, and synthetic fiber spinning under mechanical pressure.",
    zh: "适用于吹膜、再生塑料造粒及化纤纺丝的高压熔体过滤，确保连续稳定截留。",
    es: "Filtración continua de masa fundida para película soplada, peletizado de resina reciclada e hilatura de fibras sintéticas.",
    de: "Kontinuierliche Schmelzefiltration für Blasfolien, Recyclingharz-Granulierung und Chemiefaserspinnen unter Prozessdruck.",
    fr: "Filtration continue de polymères fondus pour film soufflé, granulation de résines recyclées et filage de fibres synthétiques.",
    pt: "Filtração contínua de polímero fundido para filme tubular, granulação de resina reciclada e fiação de fibras sintéticas."
  },
  micronRating: "5 - 200 µm",
  recommendedMesh: {
    en: "Sintered Multilayer Mesh & Dutch Weave Screen Packs",
    zh: "多层烧结网与密纹编织滤盘组",
    es: "Malla multicapa sinterizada y paquetes filtrantes de tejido Dutch",
    de: "Mehrlagiges Sintergewebe & Tressengewebe-Filterpakete",
    fr: "Toile multicouche frittée et packs filtrants en tissage hollandais",
    pt: "Malha multicamada sinterizada e pacotes de filtros em trama holandesa"
  },
  keyFeature: {
    en: "Rigid structure prevents wire movement under process pressure",
    zh: "刚性烧结结构，工艺压差下网丝稳定不移位",
    es: "Estructura rígida que evita el desplazamiento del alambre bajo presión",
    de: "Formstabile Struktur verhindert Drahtverschiebung unter Prozessdruck",
    fr: "Structure rigide empêchant le déplacement des fils sous pression de procédé",
    pt: "Estrutura rígida evita movimentação dos fios sob pressão de processo"
  },
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/app_polymer_extrusion.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/app_polymer_extrusion.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/app_polymer_extrusion.webp-wvc-sizes",
    alt: {
      en: "Polymer melt extrusion mesh screen pack filtration",
      zh: "高分子塑料挤出过滤网组",
      es: "Paquete de mallas para filtración de extrusión de polímeros fundidos",
      de: "Siebpaket für Polymerschmelzen-Extrusionsfiltration",
      fr: "Pack de toiles pour filtration d'extrusion de polymères fondus",
      pt: "Pacote de telas para filtração de extrusão de polímeros fundidos"
    }
  },
  link: "/PolymerMeltFiltrationApplicationsPage"
}, {
  id: "petrochemical",
  category: "chemical",
  title: {
    en: "Petrochemical Process Filtration",
    zh: "石油化工工艺介质过滤",
    es: "Filtración en procesos petroquímicos",
    de: "Petrochemische Prozessfiltration",
    fr: "Filtration des procédés pétrochimiques",
    pt: "Filtração em processos petroquímicos"
  },
  description: {
    en: "Corrosion-resistant catalyst recovery and slurry oil filtration in high-temperature hydrocracking and refining units.",
    zh: "用于加氢裂化与炼化装置中的催化剂回收及油浆过滤，耐受腐蚀介质与工艺热负荷。",
    es: "Recuperación de catalizador resistente a corrosión y filtración de lodos en unidades de craqueo térmico y refinación.",
    de: "Korrosionsbeständige Katalysatorrückgewinnung und Schlammölfiltration in Hydrocrack- und Raffinerieanlagen.",
    fr: "Récupération de catalyseur résistant à la corrosion et filtration d'huile lourde en hydrocraquage et raffinage.",
    pt: "Recuperação de catalisador resistente à corrosão e filtração de óleo clarificado em refino e hidrocraqueamento."
  },
  micronRating: "2 - 100 µm",
  recommendedMesh: {
    en: "Twill Dutch Weave & 5-Layer Sintered Mesh",
    zh: "斜纹密纹编织网与五层标准烧结网",
    es: "Tejido Dutch cruzado y malla sinterizada de 5 capas",
    de: "Köpertresse & 5-lagiges Sinterdrahtgewebe",
    fr: "Tissage hollandais sergé et toile frittée 5 couches",
    pt: "Trama holandesa sarja e malha sinterizada de 5 camadas"
  },
  keyFeature: {
    en: "High corrosion resistance in harsh acidic hydrocarbon streams",
    zh: "在酸性烃类介质中具备出色的耐蚀性与结构强度",
    es: "Alta resistencia a la corrosión en corrientes de hidrocarburos ácidos",
    de: "Hohe Korrosionsbeständigkeit in aggressiven sauren Kohlenwasserstoffströmen",
    fr: "Haute résistance à la corrosion dans les flux d'hydrocarbures acides sévères",
    pt: "Alta resistência à corrosão em correntes severas de hidrocarbonetos ácidos"
  },
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/app_petrochemical_plant.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/app_petrochemical_plant.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/app_petrochemical_plant.png-wvc-sizes",
    alt: {
      en: "Petrochemical process filtration vessel with stainless steel mesh",
      zh: "石油化工工艺不锈钢过滤元件容器",
      es: "Recipiente de filtración petroquímica con malla de acero inoxidable",
      de: "Petrochemischer Filtrationsbehälter mit Edelstahl-Drahtgewebe",
      fr: "Cuve de filtration de procédé pétrochimique avec toile en acier inoxydable",
      pt: "Vaso de filtração petroquímica com malha de aço inoxidável"
    }
  },
  link: "/PetrochemicalProcessFiltrationPage"
}, {
  id: "fine-chemical",
  category: "chemical",
  title: {
    en: "Fine Chemical Processing",
    zh: "精细化工与溶剂澄清",
    es: "Procesamiento de química fina",
    de: "Feinchemie-Verarbeitung",
    fr: "Chimie fine et clarification",
    pt: "Processamento de química fina"
  },
  description: {
    en: "Solid-liquid separation and solvent clarification in batch chemical production demanding aperture uniformity and open area.",
    zh: "用于精细化工间歇生产中的固液分离与溶剂澄清，提供均一开孔精度与高通量。",
    es: "Separación sólido-líquido y clarificación de disolventes en producción química por lotes con apertura uniforme.",
    de: "Fest-Flüssig-Trennung und Lösemittelklärung in der Chargenchemie mit homogener Maschenöffnung und hohem Durchfluss.",
    fr: "Séparation solide-liquide et clarification de solvants en chimie fine exigeant une ouverture uniforme et un débit stable.",
    pt: "Separação sólido-líquido e clarificação de solventes em bateladas com abertura uniforme e alta permeabilidade."
  },
  micronRating: "10 - 310 µm",
  recommendedMesh: {
    en: "Plain Dutch Weave & Reverse Dutch Weave Mesh",
    zh: "平纹密纹编织网与反向密纹网",
    es: "Tejido Dutch liso y malla Dutch inversa",
    de: "Glatte Tresse & Panzertresse",
    fr: "Tissage hollandais uni et toile hollandaise inversée",
    pt: "Trama holandesa lisa e trama holandesa reversa"
  },
  keyFeature: {
    en: "Uniform aperture distribution for repeatable particle retention",
    zh: "规整的孔径分布，确保颗粒截留效果稳定可重复",
    es: "Distribución uniforme de aperturas para retención de partículas repetible",
    de: "Gleichmäßige Maschenweitenverteilung für reproduzierbaren Partikelrückhalt",
    fr: "Distribution uniforme des ouvertures pour une rétention particulaire reproductible",
    pt: "Distribuição uniforme de aberturas para retenção de partículas repetível"
  },
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/app_chemical_processing.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/app_chemical_processing.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/app_chemical_processing.png-wvc-sizes",
    alt: {
      en: "Industrial chemical filtration reactors and stainless steel strainers",
      zh: "精细化工反应釜与不锈钢过滤器介质",
      es: "Reactores químicos y filtros de acero inoxidable para filtración industrial",
      de: "Industrielle Chemiereaktoren und Edelstahlfilter für Trennprozesse",
      fr: "Réacteurs chimiques industriels et filtres en acier inoxydable",
      pt: "Reatores químicos industriais e filtros em aço inoxidável"
    }
  },
  link: "/ChemicalFiltrationPage"
}, {
  id: "pharmaceutical",
  category: "pharma",
  title: {
    en: "Pharmaceutical & Sanitary Straining",
    zh: "医药原料与洁净流体过滤",
    es: "Filtración farmacéutica y sanitaria",
    de: "Pharmazeutische & sanitäre Filtration",
    fr: "Filtration pharmaceutique et sanitaire",
    pt: "Filtração farmacêutica e sanitária"
  },
  description: {
    en: "Active pharmaceutical ingredient solid isolation, nutsche filter dryer plates, and sanitary liquid straining with smooth finishes.",
    zh: "用于原料药结晶固液分离、抽滤干燥三合一多孔滤板及洁净液体精细过滤。",
    es: "Aislamiento de principios activos sólidos, placas para secadores filtro nutsche y filtración sanitaria de líquidos.",
    de: "Wirkstoff-Feststofftrennung, Nutschenfilter-Trocknerplatten und hygienische Flüssigkeitsfiltration mit glatter Oberfläche.",
    fr: "Isolement de principes actifs solides, plaques de filtres sécheurs nutsche et filtration de liquides sanitaires.",
    pt: "Isolamento de sólidos em IFA, placas de secador filtro nutsche e filtração sanitária de líquidos."
  },
  micronRating: "1 - 150 µm",
  recommendedMesh: {
    en: "Smooth Sintered Laminate Panels & Dutch Weave Cloth",
    zh: "光滑烧结复合网板与密纹编织过滤布",
    es: "Placas de laminado sinterizado liso y tela tejida Dutch",
    de: "Glatte Sinterlaminat-Platten & Tressengewebe-Filtertuch",
    fr: "Plaques stratifiées frittées lisses et toile en tissage hollandais",
    pt: "Placas laminadas sinterizadas lisas e tecido em trama holandesa"
  },
  keyFeature: {
    en: "Smooth surface finish reduces particle entrapment during washing",
    zh: "表面平整光滑，便于滤饼洗涤且无介质纤维脱落",
    es: "Acabado superficial liso que reduce el atrapamiento de partículas durante el lavado",
    de: "Glatte Oberflächenbeschaffenheit verringert Partikelanhaftung beim Waschen",
    fr: "Finition de surface lisse réduisant le piégeage des particules au lavage",
    pt: "Acabamento superficial liso reduz o aprisionamento de partículas na lavagem"
  },
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/app_pharma_sanitary.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/app_pharma_sanitary.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/app_pharma_sanitary.webp-wvc-sizes",
    alt: {
      en: "Pharmaceutical sanitary stainless steel filter plate in cleanroom",
      zh: "医药洁净环境不锈钢烧结过滤板",
      es: "Placa filtrante sanitaria de acero inoxidable en sala limpia",
      de: "Hygienische Edelstahl-Filterplatte für pharmazeutische Reinräume",
      fr: "Plaque filtrante sanitaire en acier inoxydable en salle propre",
      pt: "Placa filtrante sanitária em aço inoxidável em sala limpa"
    }
  },
  link: "/PharmaceuticalFiltrationPage"
}, {
  id: "water-treatment",
  category: "water",
  title: {
    en: "Industrial Water & Wastewater",
    zh: "工业水处理与污水固液分离",
    es: "Agua industrial y aguas residuales",
    de: "Industriewasser & Abwasseraufbereitung",
    fr: "Eau industrielle et eaux usées",
    pt: "Água industrial e efluentes"
  },
  description: {
    en: "Intake screening, cooling water strainers, and backwashable separation elements designed for high solids loads.",
    zh: "用于取水预过滤、循环冷却水滤器及自清洗反冲洗装置，适应重度固体杂质负料。",
    es: "Tamizado de toma, filtros de agua de refrigeración y elementos lavables a contracorriente para cargas altas de sólidos.",
    de: "Einlauffiltration, Kühlwasserfilter und rückspülbare Trennelemente für hohe Feststoffbelastungen.",
    fr: "Criblage de prise d'eau, filtres d'eau de refroidissement et éléments rétro-lavables pour charges solides élevées.",
    pt: "Peneiramento de captação, filtros de água de resfriamento e elementos retro-laváveis para altas cargas de sólidos."
  },
  micronRating: "20 - 2000 µm",
  recommendedMesh: {
    en: "Wedge Wire Screen Cylinders & Square Mesh Baskets",
    zh: "楔形丝滤筒与不锈钢方眼网过滤蓝",
    es: "Cilindros de tamiz de alambre en cuña y cestas de malla cuadrada",
    de: "Spaltsieb-Zylinder & Quadratmaschen-Filterkörbe",
    fr: "Cylindres en fil profilé et paniers filtrants à mailles carrées",
    pt: "Cilindros de tela em arame em cunha e cestos de malha quadrada"
  },
  keyFeature: {
    en: "V-wire continuous slot profile resists particulate clogging",
    zh: "V型楔形截面连续缝隙，具有良好抗堵塞与反洗性能",
    es: "Perfil de ranura continua en V que resiste la colmatación de partículas",
    de: "V-Draht-Profil mit kontinuierlichem Spalt verhindert Verstopfung durch Feststoffe",
    fr: "Profil continu en fil en V résistant au colmatage particulaire",
    pt: "Perfil contínuo de arame em V resistente ao entupimento por partículas"
  },
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/app_water_treatment.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/app_water_treatment.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/app_water_treatment.webp-wvc-sizes",
    alt: {
      en: "Industrial water treatment plant wedge wire screen filters",
      zh: "工业水处理楔形丝不锈钢滤筒与过滤网",
      es: "Filtros de tamiz de alambre en cuña para tratamiento de agua industrial",
      de: "Spaltsiebfilter für industrielle Wasseraufbereitungsanlagen",
      fr: "Filtres à fil profilé pour stations de traitement d'eau industrielle",
      pt: "Filtros de tela de arame em cunha para tratamento de água industrial"
    }
  },
  link: "/IndustrialWaterFiltrationPage"
}, {
  id: "food-beverage",
  category: "pharma",
  title: {
    en: "Food & Beverage Clarification",
    zh: "食品饮料澄清与物料截留",
    es: "Clarificación de alimentos y bebidas",
    de: "Lebensmittel- & Getränkeklärung",
    fr: "Clarification agroalimentaire et boissons",
    pt: "Clarificação de alimentos e bebidas"
  },
  description: {
    en: "Juice clarification, edible oil polishing, and syrup straining elements made with stainless alloys without media migration.",
    zh: "用于果汁澄清、食用油抛光及糖浆过滤截留，采用全金属结构杜绝纤维杂质脱落。",
    es: "Clarificación de jugos, pulido de aceite comestible y filtración de jarabes sin migración de partículas.",
    de: "Saftklärung, Speiseöl-Feinfiltration und Sirup-Siebung aus Edelstahllegierungen ohne Medienmigration.",
    fr: "Clarification de jus, polissage d'huiles alimentaires et tamisage de sirops sans migration de média.",
    pt: "Clarificação de sucos, polimento de óleos comestíveis e coagem de xaropes sem migração de material."
  },
  micronRating: "15 - 800 µm",
  recommendedMesh: {
    en: "Plain Weave & Five Heddle Woven Wire Cloth",
    zh: "平纹编织网与五综编织不锈钢滤网",
    es: "Tejido liso y tela metálica de cinco lizos",
    de: "Leinwandbindung & Fünfschaftgewebe aus Edelstahl",
    fr: "Tissage uni et toile métallique à cinq lisses",
    pt: "Trama lisa e malha de arame em trama de cinco liços"
  },
  keyFeature: {
    en: "Clean metallic wire structure prevents media migration",
    zh: "刚性金属丝网结构，避免过滤介质迁移污染介质",
    es: "Estructura metálica limpia que evita la migración de partículas del medio",
    de: "Reine metallische Drahtstruktur verhindert Partikel- und Medienmigration",
    fr: "Structure métallique propre éliminant toute migration de média filtrant",
    pt: "Estrutura metálica pura evita qualquer migração de fibras ou partículas"
  },
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/app_food_processing.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/app_food_processing.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/app_food_processing.webp-wvc-sizes",
    alt: {
      en: "Food processing liquid straining mesh basket filter",
      zh: "食品加工液体过滤不锈钢滤篮",
      es: "Cesta filtrante de malla para filtración de líquidos en alimentos",
      de: "Edelstahl-Filterkorb für die Lebensmittel-Flüssigkeitsfiltration",
      fr: "Panier filtrant en toile métallique pour liquides agroalimentaires",
      pt: "Cesto filtrante em malha para clarificação de líquidos alimentícios"
    }
  },
  link: "/FoodProcessingFiltrationPage"
}];
export default function Applications() {
  const {
    lang
  } = useLanguage();
  const [activeTab, setActiveTab] = useState<string>("all");
  const copy = useMemo(() => ({
    eyebrow: pick(lang, {
      en: "Industrial Filtration Sectors",
      zh: "工业过滤与分离领域",
      es: "Sectores de filtración industrial",
      de: "Industrielle Filtrationsbereiche",
      fr: "Secteurs de filtration industrielle",
      pt: "Setores de filtração industrial"
    }),
    heading: pick(lang, {
      en: "Engineered wire mesh for critical process applications",
      zh: "满足严苛工艺环境的不锈钢丝网工程应用",
      es: "Malla de alambre técnica para aplicaciones críticas de proceso",
      de: "Technisches Drahtgewebe für kritische Prozessanwendungen",
      fr: "Toiles métalliques techniques pour procédés industriels critiques",
      pt: "Malha de arame técnica para aplicações críticas de processo"
    }),
    lead: pick(lang, {
      en: "From high-pressure polymer extrusion to harsh petrochemical synthesis, MutualFilter delivers precision woven and sintered stainless steel filtration media tailored to severe operational conditions.",
      zh: "从高压熔体挤出到腐蚀性化工介质固液分离，MutualFilter 提供精准规格编织网、烧结多层网与全焊接楔形丝滤芯，匹配多工况严苛工艺要求。",
      es: "Desde la extrusión de polímeros a alta presión hasta la síntesis petroquímica agresiva, MutualFilter suministra medios filtrantes tejidos y sinterizados adaptados a condiciones operativas severas.",
      de: "Von Hochdruck-Polymerextrusion bis zu rauen petrochemischen Prozessen liefert MutualFilter präzise gewebte und gesinterte Edelstahl-Filtermedien für anspruchsvollste Betriebsbedingungen.",
      fr: "De l'extrusion de polymères sous haute pression aux procédés pétrochimiques agressifs, MutualFilter conçoit des toiles tissées et frittées adaptées aux conditions opérationnelles sévères.",
      pt: "Da extrusão de polímeros sob alta pressão à síntese petroquímica agressiva, a MutualFilter fornece meios filtrantes tecidos e sinterizados dimensionados para condições operacionais severas."
    }),
    mediaSpec: pick(lang, {
      en: "Media Spec:",
      zh: "适配滤材规格:",
      es: "Especificación:",
      de: "Medienspezifikation:",
      fr: "Spécification:",
      pt: "Especificação:"
    }),
    viewDetails: pick(lang, {
      en: "View Application Details",
      zh: "查看应用详情",
      es: "Ver detalles de aplicación",
      de: "Anwendungsdetails anzeigen",
      fr: "Voir les détails de l'application",
      pt: "Ver detalhes da aplicação"
    }),
    filterGroupAria: pick(lang, {
      en: "Filter applications by industry sector",
      zh: "按行业领域筛选应用",
      es: "Filtrar aplicaciones por sector industrial",
      de: "Anwendungen nach Industriezweig filtern",
      fr: "Filtrer les applications par secteur industriel",
      pt: "Filtrar aplicações por setor industrial"
    }),
    inlinePrompt: pick(lang, {
      en: "Need custom mesh geometries, alloy selection, or tailored screen pack prototypes for your process?",
      zh: "需要定制丝网几何结构、合金选型或成套工艺滤片样品试制？",
      es: "¿Necesita geometrías de malla a medida, selección de aleación o prototipos de paquetes filtrantes?",
      de: "Benötigen Sie kundenspezifische Gewebegeometrien, Legierungsauswahl oder Siebpaket-Muster?",
      fr: "Besoin de géométries de toile sur mesure, du choix d'un alliage ou d'échantillons de packs filtrants?",
      pt: "Necessita de geometrias sob medida, seleção de ligas ou protótipos de pacotes de telas?"
    })
  }), [lang]);
  const tabs = useMemo(() => [{
    id: "all",
    label: pick(lang, {
      en: "All Sectors",
      zh: "全部应用领域",
      es: "Todos los sectores",
      de: "Alle Bereiche",
      fr: "Tous les secteurs",
      pt: "Todos os setores"
    }),
    icon: SlidersHorizontal
  }, {
    id: "polymer",
    label: pick(lang, {
      en: "Polymer & Recycling",
      zh: "高分子与挤出回收",
      es: "Polímeros y reciclaje",
      de: "Polymer & Recycling",
      fr: "Polymères et recyclage",
      pt: "Polímeros e reciclagem"
    }),
    icon: Layers
  }, {
    id: "chemical",
    label: pick(lang, {
      en: "Chemical & Refining",
      zh: "化工与炼油分离",
      es: "Química y refino",
      de: "Chemie & Raffination",
      fr: "Chimie et raffinage",
      pt: "Química e refino"
    }),
    icon: FlaskConical
  }, {
    id: "pharma",
    label: pick(lang, {
      en: "Pharma & Food",
      zh: "制药与食品加工",
      es: "Farmacia y alimentación",
      de: "Pharma & Lebensmittel",
      fr: "Pharma et agroalimentaire",
      pt: "Farmacêutica e alimentos"
    }),
    icon: ShieldCheck
  }, {
    id: "water",
    label: pick(lang, {
      en: "Water & Separation",
      zh: "水处理与固液分离",
      es: "Agua y separación",
      de: "Wasser & Fest-Flüssig-Trennung",
      fr: "Eau et séparation",
      pt: "Água e separação"
    }),
    icon: Droplets
  }], [lang]);
  const filteredApps = useMemo(() => activeTab === "all" ? applicationsData : applicationsData.filter(app => app.category === activeTab), [activeTab]);
  return <section id="applications" data-nav="light" className="relative mf-section bg-background text-foreground">
      <div className="mf-container">
        {/* Centered Header Block */}
        <Reveal className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-border bg-muted text-foreground mf-caption font-mono font-medium mb-4">
            <Factory className="size-4 text-primary shrink-0" aria-hidden="true" />
            <span>{copy.eyebrow}</span>
          </div>

          <h2 className="mf-h2 text-foreground font-default">
            {copy.heading}
          </h2>

          <p className="mt-4 mf-lead text-muted-foreground">
            {copy.lead}
          </p>

          {/* Application Category Filter Tabs */}
          <div role="group" aria-label={copy.filterGroupAria} className="mt-8 flex flex-wrap justify-center gap-2.5 sm:gap-3">
            {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return <Button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} aria-pressed={isActive} variant={isActive ? "default" : "outline"} className={`px-4 text-sm font-medium transition-colors border cursor-pointer inline-flex items-center gap-2 min-h-[44px] ${isActive ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:bg-muted hover:text-foreground"}`}>
                  <Icon className="size-4 shrink-0" aria-hidden="true" />
                  <span>{tab.label}</span>
                </Button>;
          })}
          </div>
        </Reveal>

        {/* 3-Column Applications Grid */}
        <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {filteredApps.map((item, idx) => <Reveal key={item.id} delay={Math.min(idx * 60, 300)}>
              <Card className="flex flex-col justify-between h-full border-border bg-card rounded-lg overflow-hidden transition-colors mf-hover-lift">
                <div className="flex flex-col">
                  {/* Image Frame */}
                  <div className="relative mf-card-media mf-ratio-card w-full overflow-hidden">
                    <img src={item.image.src} data-wvc-srcset={item.image.srcset} data-wvc-sizes={item.image.sizes} alt={pick(lang, item.image.alt)} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                    <div className="absolute top-3 left-3">
                      <Badge variant="outline" className="bg-card text-foreground font-mono mf-caption border-border py-1 px-2.5">
                        {item.micronRating}
                      </Badge>
                    </div>
                  </div>

                  <CardHeader className="pt-5 pb-2">
                    <CardTitle className="text-lg font-bold text-foreground">
                      {pick(lang, item.title)}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4 text-sm">
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {pick(lang, item.description)}
                    </CardDescription>

                    <div className="space-y-2.5 pt-3 border-t border-border">
                      <div className="flex items-start gap-2">
                        <span className="mf-caption font-semibold uppercase text-muted-foreground shrink-0 w-28">
                          {copy.mediaSpec}
                        </span>
                        <span className="text-sm font-medium text-foreground">
                          {pick(lang, item.recommendedMesh)}
                        </span>
                      </div>
                      <div className="flex items-start gap-2 text-sm text-foreground font-medium">
                        <CircleCheck className="size-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                        <span>{pick(lang, item.keyFeature)}</span>
                      </div>
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="pt-4 pb-4 border-t border-border bg-muted/20 mt-auto">
                  <Button asChild variant="ghost" className="w-full justify-between text-sm font-medium text-foreground hover:text-primary hover:bg-transparent px-2 min-h-[44px]">
                    <Link to={item.link}>
                      <span>{copy.viewDetails}</span>
                      <ArrowRight className="size-4" aria-hidden="true" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </Reveal>)}
        </div>

        {/* Mid-page single InlineCta prompt following standard */}
        <Reveal delay={120} className="mt-12 sm:mt-16">
          <InlineCta text={copy.inlinePrompt} buttonLabel={pick(lang, COMMON.requestQuote)} to="/RequestAQuotePage" />
        </Reveal>
      </div>
    </section>;
}